import React, { useState } from 'react';
import { financialTransactionAPI } from '../financialTransactionService';
import toast from 'react-hot-toast';
import { X, Loader, CreditCard, Banknote, Building2 } from 'lucide-react';
import { btn } from '../../utils/buttonStyles';

const PAYMENT_METHODS = [
  { id: 'cash',          label: 'Cash',   icon: <Banknote className="w-5 h-5" /> },
  { id: 'zelle',         label: 'Zelle',  icon: <CreditCard className="w-5 h-5" /> },
  { id: 'venmo',         label: 'Venmo',  icon: <CreditCard className="w-5 h-5" /> },
  { id: 'paypal',        label: 'PayPal', icon: <CreditCard className="w-5 h-5" /> },
  { id: 'bank_transfer', label: 'Bank',   icon: <Building2 className="w-5 h-5" /> },
  { id: 'other',         label: 'Other',  icon: <CreditCard className="w-5 h-5" /> },
];

/**
 * SettleUpModal
 *
 * Props:
 *   target   – { creditorName, amount, creditTransactions } | null  (null = hidden)
 *   onClose  – called to dismiss the modal
 *   onSuccess – called after payment is successfully recorded
 *   theme    – theme object from ThemeContext
 */
const SettleUpModal = ({ target, onClose, onSuccess, theme }) => {
  const [paymentMethod, setPaymentMethod] = useState('');
  const [settling, setSettling]           = useState(false);

  if (!target) return null;

  const handleConfirm = async () => {
    if (!paymentMethod) return;
    setSettling(true);
    try {
      const now = new Date().toISOString();
      await Promise.all(
        target.creditTransactions.map(ct =>
          financialTransactionAPI.createPayment({
            creditTransactionId: ct.creditTransactionId,
            amount:              ct.amount,
            paymentMethod,
            paymentDate:         now,
          })
        )
      );
      toast.success('Payment recorded!');
      setPaymentMethod('');
      onClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || 'Failed to record payment');
    } finally {
      setSettling(false);
    }
  };

  const handleClose = () => {
    setPaymentMethod('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-2xl w-full max-w-sm border ${theme.border} shadow-2xl`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-white">Settle Up</h3>
            <p className="text-blue-100 text-xs mt-0.5">Paying {target.creditorName}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Amount */}
          <div className={`rounded-xl ${theme.backgroundSecondary} border ${theme.border} px-4 py-3 flex items-center justify-between`}>
            <span className={`text-sm ${theme.textSecondary}`}>Amount</span>
            <span className="text-2xl font-bold text-red-500">${target.amount.toFixed(0)}</span>
          </div>

          {/* Payment method picker */}
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-3`}>How did you pay?</p>
            <div className="grid grid-cols-3 gap-2">
              {PAYMENT_METHODS.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setPaymentMethod(m.id)}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-xs font-semibold transition-all ${
                    paymentMethod === m.id
                      ? 'border-blue-500 bg-blue-500/10 text-blue-500'
                      : `${theme.border} ${theme.card} ${theme.textSecondary}`
                  }`}
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!paymentMethod || settling}
              className={`flex-1 flex items-center justify-center gap-2 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
            >
              {settling && <Loader className="w-4 h-4 animate-spin" />}
              {settling ? 'Recording…' : 'Confirm Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettleUpModal;
